import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { api, API } from "../lib/api";
import { useAuth } from "../lib/auth";
import { SectionHeader } from "../components/UI";
import { TID } from "../constants/testIds";
import { toast } from "sonner";
import { CreditCard, Download, CheckCircle2, ArrowUpRight, ArrowDownRight, Sparkles } from "lucide-react";

const PLAN_ORDER = ["starter", "professional", "enterprise"];

export default function Billing() {
  const { tenant, refreshMe } = useAuth();
  const [plans, setPlans] = useState([]);
  const [subscription, setSubscription] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [changing, setChanging] = useState(false);

  const load = async () => {
    try {
      const [{ data: p }, { data: s }, { data: inv }] = await Promise.all([
        api.get("/billing/plans"),
        api.get("/billing/subscription"),
        api.get("/billing/invoices"),
      ]);
      setPlans(p);
      setSubscription(s);
      setInvoices(inv);
    } catch { toast.error("Failed to load billing data"); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const currentIdx = subscription ? PLAN_ORDER.indexOf(subscription.plan) : -1;

  const changePlan = async (plan) => {
    if (plan === subscription?.plan) return;
    const isUpgrade = PLAN_ORDER.indexOf(plan) > currentIdx;
    const label = isUpgrade ? "upgrade" : "downgrade";
    if (!window.confirm(`Confirm ${label} to the ${plan.toUpperCase()} plan?`)) return;
    setChanging(true);
    try {
      const { data } = await api.post("/billing/subscription", { plan });
      toast.success(`${label.charAt(0).toUpperCase() + label.slice(1)}d to ${plan}. Invoice ${data.invoice.invoice_number} generated.`);
      await load();
      refreshMe();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setChanging(false); }
  };

  const downloadInvoice = async (inv) => {
    try {
      const token = localStorage.getItem("prophr_token");
      const res = await fetch(`${API}/billing/invoices/${inv.id}/download`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Download failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${inv.invoice_number}.txt`;
      a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Failed to download invoice"); }
  };

  return (
    <div className="p-8 max-w-6xl mx-auto">
      <SectionHeader
        title="Billing & Subscription"
        subtitle="Manage your PropHR plan, view invoices, and control your workspace subscription."
      />

      {/* Current subscription banner */}
      {subscription?.plan ? (
        <motion.div
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          className="ai-surface tracing-beam p-6 mb-6"
        >
          <div className="flex items-center gap-2 mb-1">
            <Sparkles size={14} className="text-blue-300" strokeWidth={2}/>
            <span className="eyebrow text-blue-300">Current subscription</span>
          </div>
          <div className="flex items-end justify-between flex-wrap gap-4">
            <div>
              <div className="font-editorial text-4xl text-slate-100 tracking-tight capitalize">{subscription.plan}</div>
              <div className="text-sm text-slate-400 mt-0.5">{tenant?.name}</div>
            </div>
            <div className="text-right">
              <div className="font-display text-3xl font-semibold text-slate-100">${subscription.monthly}<span className="text-sm font-normal text-slate-400">/mo</span></div>
              <div className="text-[10px] font-mono text-slate-500 uppercase tracking-widest mt-1">USD · billed monthly</div>
            </div>
          </div>
          <div className="mt-5 pt-5 border-t border-slate-800 grid md:grid-cols-2 gap-2">
            {subscription.features.map((f) => (
              <div key={f} className="text-[13px] text-slate-300 flex items-center gap-2">
                <CheckCircle2 size={12} strokeWidth={2} className="text-blue-400 shrink-0"/> {f}
              </div>
            ))}
          </div>
        </motion.div>
      ) : (
        <div className="border border-slate-200 p-6 mb-6 text-slate-600">No active subscription</div>
      )}

      {/* Plan cards */}
      <div className="grid md:grid-cols-3 gap-4 mb-8">
        {plans.map((p) => {
          const isCurrent = p.plan === subscription?.plan;
          const isUpgrade = PLAN_ORDER.indexOf(p.plan) > currentIdx;
          return (
            <motion.div
              key={p.plan}
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              className={"border p-5 flex flex-col " + (isCurrent ? "border-blue-600 shadow-lg bg-blue-50/30" : "border-slate-200 bg-white")}
              data-testid={`billing-plan-${p.plan}`}
            >
              <div className="flex items-center justify-between mb-2">
                <div className="eyebrow capitalize">{p.plan}</div>
                {isCurrent && <span className="pill pill-approved">Current</span>}
              </div>
              <div className="font-display text-3xl font-semibold text-slate-900 mb-1">${p.monthly}<span className="text-sm text-slate-500 font-normal">/mo</span></div>
              <div className="text-[11px] font-mono text-slate-500 uppercase tracking-wider mb-4">USD · monthly</div>

              <ul className="space-y-2 text-[13px] text-slate-700 mb-6 flex-1">
                {p.features.map((f) => (
                  <li key={f} className="flex items-start gap-2">
                    <CheckCircle2 size={12} strokeWidth={2} className="text-blue-600 mt-1 shrink-0"/> {f}
                  </li>
                ))}
              </ul>

              {isCurrent ? (
                <button className="btn-ghost w-full !cursor-default" disabled>Active plan</button>
              ) : (
                <button
                  className="btn-primary w-full"
                  onClick={() => changePlan(p.plan)}
                  disabled={changing}
                  data-testid={TID.billingUpgradeBtn(p.plan)}
                >
                  {isUpgrade ? (<><ArrowUpRight size={14} strokeWidth={2}/> Upgrade</>) : (<><ArrowDownRight size={14} strokeWidth={2}/> Downgrade</>)}
                </button>
              )}
            </motion.div>
          );
        })}
      </div>

      {/* Invoices */}
      <div className="data-cell p-5">
        <div className="flex items-center gap-2 mb-4">
          <CreditCard size={16} strokeWidth={1.75}/>
          <div className="eyebrow">Invoices</div>
          <span className="text-xs text-slate-500 font-mono">({invoices.length})</span>
        </div>
        {invoices.length === 0 ? (
          <div className="text-sm text-slate-500 text-center py-6">No invoices yet. Change your plan to generate one.</div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left border-b border-slate-200">
                <th className="py-2 text-[10px] font-mono uppercase tracking-wider text-slate-500">Number</th>
                <th className="py-2 text-[10px] font-mono uppercase tracking-wider text-slate-500">Plan</th>
                <th className="py-2 text-[10px] font-mono uppercase tracking-wider text-slate-500">Amount</th>
                <th className="py-2 text-[10px] font-mono uppercase tracking-wider text-slate-500">Issued</th>
                <th className="py-2 text-[10px] font-mono uppercase tracking-wider text-slate-500">Status</th>
                <th className="py-2 text-[10px] font-mono uppercase tracking-wider text-slate-500">Actions</th>
              </tr>
            </thead>
            <tbody>
              {invoices.map((inv) => (
                <tr key={inv.id} className="border-b border-slate-100 last:border-0">
                  <td className="py-2.5 font-mono text-[12px]">{inv.invoice_number}</td>
                  <td className="py-2.5 capitalize">{inv.plan}</td>
                  <td className="py-2.5 font-mono">${inv.amount} {inv.currency}</td>
                  <td className="py-2.5 text-slate-500">{new Date(inv.issued_at).toLocaleDateString()}</td>
                  <td className="py-2.5"><span className={"pill " + (inv.status === "paid" ? "pill-approved" : "pill-pending")}>{inv.status}</span></td>
                  <td className="py-2.5">
                    <button className="btn-ghost text-xs" onClick={() => downloadInvoice(inv)} data-testid={TID.billingInvoiceDownload(inv.id)}>
                      <Download size={12}/> Download
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
